import React, { useState } from 'react';
import { X, CheckCircle, Loader2, Zap, RefreshCw, Save, Mail } from 'lucide-react';
import { sendExtractorInterest } from '../utils/sendExtractorInterest';

const ExtractorInterestModal = ({ isOpen, onClose, plausible }) => {
  const [formData, setFormData] = useState({
    email: '',
    tool: '',
    customTool: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const tools = [
    { value: 'jira', label: 'Jira' },
    { value: 'azure-devops', label: 'Azure DevOps' },
    { value: 'outros', label: 'Outros' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    if (!formData.tool) {
      newErrors.tool = 'Selecione uma ferramenta';
    }
    
    if (formData.tool === 'outros' && !formData.customTool.trim()) {
      newErrors.customTool = 'Especifique a ferramenta utilizada';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Enviar interesse para o email
      await sendExtractorInterest({
        email: formData.email,
        tool: formData.tool,
        customTool: formData.customTool
      });
      
      // Track successful submission
      if (plausible) {
        plausible.trackExtractorInterest(formData.tool);
      }
      
      setIsSuccess(true);
    } catch (error) {
      console.error('Erro ao enviar interesse:', error);
      setSubmitError('Erro ao registrar interesse. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo quando usuario começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      tool: '',
      customTool: ''
    });
    setErrors({});
    setSubmitError('');
    setIsSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto relative">
        {/* Close Button - Floating */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all duration-200"
        >
          <X size={18} />
        </button>

        {/* Content */}
        <div className="p-6">
          {!isSuccess ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Seção Esquerda - Informações */}
              <div className="space-y-6">
                {/* Descrição */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Automatize a extração dos seus dados!
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Conecte-se diretamente ao <strong>Jira</strong> ou <strong>Azure DevOps</strong> e 
                    extraia automaticamente os dados dos seus projetos. Importe no Dash Simples e visualize as métricas com praticidade e eficiência.
                  </p>
                </div>

                {/* Cards de Benefícios */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-800 mb-3">✨ Benefícios:</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Zap className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-800">Conecte com facilidade</p>
                        <p className="text-xs text-blue-600">Jira e Azure DevOps em poucos cliques</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <RefreshCw className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-green-800">Elimine trabalho manual</p>
                        <p className="text-xs text-green-600">Coleta de dados 100% automatizada</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Save className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-purple-800">Visualize insights em segundos</p>
                        <p className="text-xs text-purple-600">Dados estruturados para análise no Dash Simples</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção Direita - Formulário */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">🔔 Quer ser avisado quando for lançado?</h4>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* E-mail */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="seu.email@empresa.com"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Ferramenta */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Qual ferramenta você usa? *
                    </label>
                    <select
                      value={formData.tool}
                      onChange={(e) => handleInputChange('tool', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                        errors.tool ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting}
                    >
                      <option value="">Selecione uma opção</option>
                      {tools.map(tool => (
                        <option key={tool.value} value={tool.value}>
                          {tool.label}
                        </option>
                      ))}
                    </select>
                    {errors.tool && (
                      <p className="text-red-500 text-xs mt-1">{errors.tool}</p>
                    )}
                  </div>

                  {/* Campo customizado para "Outros" */}
                  {formData.tool === 'outros' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Especifique a ferramenta *
                      </label>
                      <input
                        type="text"
                        value={formData.customTool}
                        onChange={(e) => handleInputChange('customTool', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.customTool ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ex: ClickUp, Trello..."
                        disabled={isSubmitting}
                      />
                      {errors.customTool && (
                        <p className="text-red-500 text-xs mt-1">{errors.customTool}</p>
                      )}
                    </div>
                  )}

                  {/* Erro de envio */}
                  {submitError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm">{submitError}</p>
                    </div>
                  )}

                  {/* Botão de submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Processando
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5" />
                        Me avise quando estiver pronto
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            /* Tela de sucesso */
            <div className="text-center py-12 max-w-2xl mx-auto">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-3">
                🎉 Você está na fila!
              </h3>
              <p className="text-gray-600 mb-6 text-lg">
                Obrigado pelo seu interesse! Você receberá um e-mail quando o 
                extrator estiver disponível.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                <p className="text-green-700">
                  <strong>E-mail:</strong> {formData.email}<br/>
                  <strong>Ferramenta:</strong> {formData.tool === 'outros' ? formData.customTool : tools.find(t => t.value === formData.tool)?.label}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-8 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExtractorInterestModal;
